<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import PermissionLock from '../PermissionLock.vue';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { GradeSelect } from '@ilteducation/select/grade';
import { checkGroupUpdatePermission } from '@ilteducation/customer-mgmt-permissions';

import useToast from '../../composables/useToast';
import { useComboboxFieldName, useComboboxName } from '../../composables/useComboboxName';
import { useLocale } from '../../composables/useLocale';
import { maxLength, minLength, required } from '../../rules';
import { useManageGroup } from '../../composables/useManageGroup';
import { usePermissions } from '../../composables/usePermissions';

const { t } = useLocale();
const { permissionContext, reporterWithReason, getCallerAssociation, getGroupMemberships } = usePermissions();

type FormGroup = {
  name: string;
  type?: CustomerMgmtTypes.GroupType | null;
  yearsInSchool?: number | null;
  expiresAt?: Date | null; // v-date-input model; GroupOutput.expiresAt is epoch ms
};

// v-date-input emits a Date (picked day at local midnight). Send an ISO UTC string at
// end-of-day, matching the subscription end-date convention (all dates go to the API as ISO UTC).
const toExpiresAtIso = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

// Earliest selectable expiry is start of today
const minExpiryDate = new Date();
minExpiryDate.setHours(0, 0, 0, 0);

type GroupTypes = {
  value: CustomerMgmtTypes.GroupType;
  title: string;
};

const props = defineProps<{ group: CustomerMgmtTypes.GetGroupResponse }>();

const groupEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: false, reason: 'not_permitted' });
watch(
  () => props.group,
  () => {
    checkGroupUpdatePermission(
      permissionContext.value,
      {
        ...props.group,
        organization: props.group.schoolUnit,
      },
      { name: 'fakeGroupNameForCheck' },
      getCallerAssociation,
      getGroupMemberships,
      reporterWithReason,
    ).then((res) => {
      groupEditableCheck.value = res;
    });
  },
  { immediate: true },
);

const getFormDefault = () => ({
  name: props?.group?.name || '',
  type: props?.group?.type ?? null,
  yearsInSchool: props?.group?.yearsInSchool ?? null,
  expiresAt: props?.group?.expiresAt ? new Date(props.group.expiresAt) : null,
});

const formRef = ref();
const formGroup = ref<FormGroup>(getFormDefault());

watch(formGroup.value, () => {
  formRef.value?.validate();
});

const isUpdating = ref(false);

const { toast } = useToast();
const { usedNames, ...manageGroup } = useManageGroup();

const groupTypes: GroupTypes[] = [
  { value: 'class', title: t('lcp.class') },
  { value: 'subject', title: t('lcp.subject') },
];

const groupTypeMenuProps = useComboboxName(() => t('lcp.group_type'));
const expiryField = useComboboxFieldName(() => t('lcp.expiry_date'));

const usedNamesRule = (value: string) => (usedNames.value.includes(value) ? t('lcp.group_name_taken') : true);
const nameRules = [required(t), minLength(1, t), maxLength(255, t), usedNamesRule];

const isGroupInfoChanged = computed(() => {
  return (
    formGroup.value.name !== props.group.name ||
    formGroup.value.type !== props.group.type ||
    formGroup.value.yearsInSchool !== props.group.yearsInSchool ||
    (formGroup.value.expiresAt?.getTime() ?? null) !== (props.group.expiresAt ?? null)
  );
});

const updateGroup = async () => {
  if (!props.group || !props.group.uid) {
    return false;
  }

  isUpdating.value = true;

  if (!formRef.value.validate()) {
    isUpdating.value = false;
    return false;
  }

  // unchanged -> undefined (omit); cleared -> null; set -> ISO UTC at end-of-day
  const newExpiresMs = formGroup.value.expiresAt?.getTime() ?? null;
  const expiresAt =
    newExpiresMs === (props.group.expiresAt ?? null)
      ? undefined
      : formGroup.value.expiresAt
        ? toExpiresAtIso(formGroup.value.expiresAt)
        : null;

  const payload = {
    name: formGroup.value.name === props.group.name ? undefined : formGroup.value.name,
    type: formGroup.value.type === props.group.type ? undefined : formGroup.value.type,
    yearsInSchool:
      formGroup.value.yearsInSchool === props.group.yearsInSchool ? undefined : formGroup.value.yearsInSchool,
    expiresAt,
  };

  const isGroupUpdated = await manageGroup.updateGroup(props?.group?.uid, payload);

  if (!isGroupUpdated) {
    formRef.value.validate();
    toast(t('lcp.group_updated_failed'), 'error');
    return false;
  }

  toast(t('lcp.group_updated_successfully'), 'success');
  isUpdating.value = false;
  return true;
};

const reset = () => {
  isUpdating.value = false;
  formRef.value.reset();
  formGroup.value = getFormDefault();
};

defineExpose({
  valid: computed(() => formRef.value?.isValid || false),
  isUpdating,
  isGroupInfoChanged,
  updateGroup,
  reset,
});
</script>

<template>
  <v-form ref="formRef">
    <v-row>
      <v-col cols="12">
        <v-row align="center" no-gutters>
          <v-col>
            <v-text-field
              variant="outlined"
              :singleLine="false"
              clearable
              clear-icon="mdi-close"
              :label="t('lcp.group_name') + ' *'"
              hide-details="auto"
              required
              :disabled="!groupEditableCheck.allowed"
              block
              v-model="formGroup.name"
              :rules="nameRules"
            >
            </v-text-field>
          </v-col>
          <v-col v-if="!groupEditableCheck.allowed" cols="auto" class="px-2">
            <PermissionLock :reason="groupEditableCheck.reason" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-select
          v-model="formGroup.type"
          name="type"
          :items="groupTypes"
          :label="t('lcp.group_type')"
          :placeholder="t('lcp.group_type')"
          :menu-props="groupTypeMenuProps"
          variant="outlined"
          density="default"
          hide-details="auto"
          bgColor="surface-bright"
          clearable
          clear-icon="mdi-close"
        ></v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <GradeSelect
          v-model="formGroup.yearsInSchool"
          name="grade"
          :label="t('lcp.grade')"
          :placeholder="t('lcp.grade')"
          variant="outlined"
          density="default"
          hide-details="auto"
          bgColor="surface-bright"
          clearable
          clear-icon="mdi-close"
        />
      </v-col>
      <v-col v-if="!groupEditableCheck.allowed" cols="auto" class="d-flex align-center justify-end pl-0">
        <PermissionLock :reason="groupEditableCheck.reason" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-date-input
          ref="expiryField"
          role="combobox"
          id="expiresAt"
          name="expiresAt"
          :disabled="!groupEditableCheck.allowed"
          v-model="formGroup.expiresAt"
          :label="t('lcp.expiry_date')"
          :min="minExpiryDate"
          variant="outlined"
          density="default"
          prepend-icon=""
          append-inner-icon="mdi-calendar"
          hide-actions
          clearable
          clear-icon="mdi-close"
          :hint="t('lcp.expiry_date_info')"
          persistent-hint
        ></v-date-input>
      </v-col>
    </v-row>
  </v-form>
</template>
